// Order tracking utilities for no-backend client-side tracking.
// Orders can be tracked two ways:
//   1. localStorage (works on the device that placed the order)
//   2. Shareable URL with a base64-encoded order payload (works anywhere)
// Status progression is derived from elapsed time + scheduled delivery date,
// so the page always shows a believable, evolving status without a server.

export interface TrackedOrderItem {
  product: string
  quantity: number
  pricePerYard: number
}

export interface TrackedOrder {
  orderId: string
  createdAt: number // ms since epoch
  address: string
  name?: string
  phone?: string
  email?: string
  date: string // ISO date string for scheduled delivery
  time?: string // human-readable window e.g. "9:00 AM - 12:00 PM"
  instructions?: string
  items: TrackedOrderItem[]
  totalDeliveryFee: number
  /**
   * Optional Saturday surcharge bundled separately so the TrackOrder page
   * can show it as its own line item instead of silently inflating the
   * delivery fee. Older saved orders won't carry this field — the display
   * code should treat undefined/0 as "no surcharge".
   */
  saturdaySurcharge?: number
  estimatedTax: number
  finalTotal: number
  /**
   * Lifecycle status. When omitted the order is treated as "active" and its
   * step is derived from elapsed time (the default behavior). When set to
   * 'canceled' the tracking page shows a canceled state instead of the live
   * delivery timeline. Because this field lives inside the order JSON, it is
   * carried through the share-link token — a link generated for a canceled
   * order reflects the cancellation anywhere it's opened.
   */
  status?: 'active' | 'canceled'
  /** ms since epoch when the order was canceled (for display). */
  canceledAt?: number
}

// ---------- Order number generation ----------

// Maps the human-readable category names from data/products.ts to compact,
// phone-friendly prefix codes. 'MX' is used when a single order spans
// multiple categories (the common case for landscape projects that mix
// gravel + topsoil). Letters chosen to avoid easily-confused pairs over
// the phone (no B/D, no P/V, no M/N).
const CATEGORY_PREFIX: Record<string, string> = {
  'Driveway & Base': 'BC',
  'Sand & Fill': 'FS',
  'Drainage & Clear Rock': 'DR',
  'Soil & Compost': 'SO',
  'Bark & Ground Cover': 'BK',
  'Large Rock & Erosion Control': 'QR',
}

// Generates a customer-facing order number like "206-BC-4821".
// The last four digits are a RANDOM number (1000–9999) per order rather than
// a per-device sequence. A local incrementing counter restarted at 0001 on
// every new device, so fresh orders kept showing up as "...-0001" — which
// made the business look brand new. A random four-digit suffix avoids that
// while keeping the category prefix meaningful. (There's no server here, so
// uniqueness isn't guaranteed, but collisions are rare and acceptable for the
// no-backend tracking flow.)
export function generateOrderNumber(
  items: { category: string; quantity: number }[],
): string {
  let prefix = 'MX'
  if (items && items.length > 0) {
    const tally: Record<string, number> = {}
    for (const it of items) {
      const code = CATEGORY_PREFIX[it.category] || 'MX'
      tally[code] = (tally[code] || 0) + (it.quantity || 0)
    }
    const codes = Object.keys(tally)
    if (codes.length === 1) {
      prefix = codes[0]
    } else {
      prefix = 'MX'
    }
  }
  const seq = Math.floor(Math.random() * 9000) + 1000
  return `206-${prefix}-${String(seq).padStart(4, '0')}`
}

const STORAGE_KEY = '253gravel_tracked_orders'
const LATEST_KEY = '253gravel_latest_order_id'

// ---------- Persistence ----------

function readAll(): Record<string, TrackedOrder> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function writeAll(orders: Record<string, TrackedOrder>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(orders))
  } catch {
    /* ignore */
  }
}

export function saveOrderForTracking(order: TrackedOrder) {
  if (!order?.orderId) return
  const all = readAll()
  all[order.orderId] = order
  writeAll(all)
  try {
    localStorage.setItem(LATEST_KEY, order.orderId)
  } catch {
    /* ignore */
  }
}

export function loadOrderById(orderId: string): TrackedOrder | null {
  if (!orderId) return null
  const all = readAll()
  return all[orderId] || null
}

/**
 * Marks a locally-tracked order as canceled and persists the change. Returns
 * the updated order (or null if the order isn't on this device). The canceled
 * status is part of the order JSON, so a freshly built share link will also
 * reflect the cancellation.
 */
export function cancelOrder(orderId: string): TrackedOrder | null {
  if (!orderId) return null
  const all = readAll()
  const existing = all[orderId]
  if (!existing) return null
  const updated: TrackedOrder = {
    ...existing,
    status: 'canceled',
    canceledAt: Date.now(),
  }
  all[orderId] = updated
  writeAll(all)
  return updated
}

export function loadLatestOrder(): TrackedOrder | null {
  if (typeof window === 'undefined') return null
  try {
    const id = localStorage.getItem(LATEST_KEY)
    if (!id) return null
    return loadOrderById(id)
  } catch {
    return null
  }
}

// ---------- Share-link token ----------
//
// Two encodings are supported here so the migration is safe:
//
//   1. NEW (v2): LZ-string compressed → URL-safe string. Cuts the
//      tracking link length roughly in half on a typical multi-item
//      order. New tokens are prefixed with `~` (a character that
//      never appears in base64-url output) so the decoder can
//      detect them without ambiguity.
//
//   2. OLD (legacy): raw JSON → base64-url-safe. Kept for backward
//      compatibility — any tracking link emailed/SMSed before this
//      change still resolves correctly.
//
// The decoder tries the prefix first; if missing, it falls back to
// the legacy base64 path.

import {
  compressToEncodedURIComponent,
  decompressFromEncodedURIComponent,
} from 'lz-string'

const V2_PREFIX = '~'

// Legacy base64-url-safe encoding (works with unicode characters).
// Still used by the decoder for older links; new links go through
// the LZ-string path above.
function b64UrlEncode(str: string): string {
  const utf8 = unescape(encodeURIComponent(str))
  return btoa(utf8).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function b64UrlDecode(token: string): string {
  const padded = token.replace(/-/g, '+').replace(/_/g, '/')
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4))
  const decoded = atob(padded + pad)
  return decodeURIComponent(escape(decoded))
}

export function encodeOrderToken(order: TrackedOrder): string {
  try {
    const json = JSON.stringify(order)
    const compressed = compressToEncodedURIComponent(json)
    // Safety net: if compression somehow produces a longer string
    // than the legacy base64 (shouldn't happen, but possible for
    // extremely tiny payloads), fall back to legacy so we never
    // make the link worse than before.
    const legacy = b64UrlEncode(json)
    if (compressed && compressed.length + V2_PREFIX.length < legacy.length) {
      return V2_PREFIX + compressed
    }
    return legacy
  } catch {
    return ''
  }
}

export function decodeOrderToken(token: string): TrackedOrder | null {
  if (!token) return null
  try {
    let json: string | null = null
    if (token.startsWith(V2_PREFIX)) {
      // New compressed format.
      json = decompressFromEncodedURIComponent(token.slice(V2_PREFIX.length))
    } else {
      // Legacy base64 format — keep working for older emailed links.
      json = b64UrlDecode(token)
    }
    if (!json) return null
    const parsed = JSON.parse(json) as TrackedOrder
    if (!parsed?.orderId) return null
    return parsed
  } catch {
    return null
  }
}

export function buildShareLink(order: TrackedOrder): string {
  const token = encodeOrderToken(order)
  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://206gravel.com'
  // BrowserRouter path — no `/#/` prefix. The order ID is already a
  // safe slug (e.g. "253-BC-0042") but we still encode it defensively
  // in case a future format introduces non-URL-safe characters.
  return `${origin}/track/${encodeURIComponent(order.orderId)}?d=${token}`
}

// ---------- Status derivation ----------

export type OrderStepId =
  | 'confirmed'
  | 'scheduled'
  | 'dispatched'
  | 'en_route'
  | 'delivered'

export const STEP_ORDER: OrderStepId[] = [
  'confirmed',
  'scheduled',
  'dispatched',
  'en_route',
  'delivered',
]

// Returns the current step index (0-4) given the order data and current time.
// Logic:
//   - Right after placement (< 2 min): "confirmed"
//   - Before delivery day:               "scheduled"
//   - On delivery day, before window:    "dispatched"
//   - During delivery window:            "en_route"
//   - After window ends:                 "delivered"
// If delivery date is missing/unparseable, falls back to elapsed time.
export function getCurrentStepIndex(
  order: TrackedOrder,
  now: number = Date.now(),
): number {
  const elapsedMs = now - (order.createdAt || now)

  // Try to parse delivery date + window
  const deliveryStart = parseDeliveryWindowStart(order.date, order.time)
  const deliveryEnd = parseDeliveryWindowEnd(order.date, order.time)

  // First 2 minutes: confirmed
  if (elapsedMs < 2 * 60 * 1000) return 0

  if (!deliveryStart || !deliveryEnd) {
    // Fallback: pure elapsed-time progression for demo / missing-date orders
    if (elapsedMs < 30 * 60 * 1000) return 1 // scheduled (< 30 min)
    if (elapsedMs < 60 * 60 * 1000) return 2 // dispatched (< 1 hr)
    if (elapsedMs < 90 * 60 * 1000) return 3 // en route (< 1.5 hr)
    return 4 // delivered
  }

  // 90 min before window start = dispatched
  if (now < deliveryStart - 90 * 60 * 1000) return 1 // scheduled
  if (now < deliveryStart) return 2 // dispatched
  if (now < deliveryEnd) return 3 // en route
  return 4 // delivered
}

// Parse a YYYY-MM-DD string as a LOCAL-time Date at midnight.
// Using `new Date("2026-05-12")` parses as UTC midnight, which in Pacific
// time becomes May 11 ~4-5pm — causing the delivery window to appear to
// have already passed and prematurely marking orders as "Delivered".
function parseLocalDate(dateStr: string): Date | null {
  // ISO date-only string (YYYY-MM-DD) — construct from local parts
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (m) {
    const y = parseInt(m[1], 10)
    const mo = parseInt(m[2], 10) - 1
    const d = parseInt(m[3], 10)
    const dt = new Date(y, mo, d, 0, 0, 0, 0)
    return isNaN(dt.getTime()) ? null : dt
  }
  // Fallback for full datetime strings — trust the parser
  const dt = new Date(dateStr)
  return isNaN(dt.getTime()) ? null : dt
}

// Try to parse the delivery window start from order.date + order.time.
// order.time looks like "9:00 AM - 12:00 PM" or "Any time".
function parseDeliveryWindowStart(
  dateStr?: string,
  timeStr?: string,
): number | null {
  if (!dateStr) return null
  const day = parseLocalDate(dateStr)
  if (!day) return null
  if (!timeStr || /any/i.test(timeStr)) {
    // Default 8 AM if no window
    day.setHours(8, 0, 0, 0)
    return day.getTime()
  }
  const startToken = timeStr.split(/[-–—]/)[0]?.trim()
  const parsed = parseClockTime(startToken)
  if (!parsed) {
    day.setHours(8, 0, 0, 0)
    return day.getTime()
  }
  day.setHours(parsed.hours, parsed.minutes, 0, 0)
  return day.getTime()
}

function parseDeliveryWindowEnd(
  dateStr?: string,
  timeStr?: string,
): number | null {
  if (!dateStr) return null
  const day = parseLocalDate(dateStr)
  if (!day) return null
  if (!timeStr || /any/i.test(timeStr)) {
    day.setHours(18, 0, 0, 0)
    return day.getTime()
  }
  const parts = timeStr.split(/[-–—]/)
  const endToken = (parts[1] || parts[0])?.trim()
  const parsed = parseClockTime(endToken)
  if (!parsed) {
    day.setHours(18, 0, 0, 0)
    return day.getTime()
  }
  day.setHours(parsed.hours, parsed.minutes, 0, 0)
  return day.getTime()
}

function parseClockTime(
  token?: string,
): { hours: number; minutes: number } | null {
  if (!token) return null
  const m = token.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i)
  if (!m) return null
  let h = parseInt(m[1], 10)
  const min = m[2] ? parseInt(m[2], 10) : 0
  const ampm = m[3]?.toLowerCase()
  if (ampm === 'pm' && h < 12) h += 12
  if (ampm === 'am' && h === 12) h = 0
  return { hours: h, minutes: min }
}

// Best-effort ETA string for the live ETA badge.
export function getEtaLabel(
  order: TrackedOrder,
  now: number = Date.now(),
): string {
  const step = getCurrentStepIndex(order, now)
  if (step === 4) return 'Delivered'
  const end = parseDeliveryWindowEnd(order.date, order.time)
  if (!end) {
    if (step === 0) return 'Just placed'
    if (step === 1) return 'Scheduled'
    if (step === 2) return '~45 min'
    return '~15 min'
  }
  const diff = end - now
  if (diff <= 0) return 'Arriving now'
  const hours = Math.floor(diff / (60 * 60 * 1000))
  const mins = Math.round((diff % (60 * 60 * 1000)) / (60 * 1000))
  if (hours > 24) return `${Math.round(hours / 24)} day(s)`
  if (hours >= 1) return `~${hours}h ${mins}m`
  return `~${mins} min`
}
